import { useEffect, useMemo, useState } from "react";

// A scheduled item is anything with an optional `publishAt` field
// (ISO date string, e.g. "2026-09-03", or a full ISO timestamp).
// No `publishAt` = already published (this keeps every existing curated
// combo working unchanged — nothing needed a date added to keep showing up).
//
// This is the ONE place that decides what's live. Every page that lists
// content (Home, CameraHub, Gallery) and every page that renders a single
// item by slug (RecipePageRoute, ViewGate) filters through this before
// using the data.
//
// -----------------------------------------------------------------------
// WHAT THIS IS AND ISN'T
//
// This is a reveal mechanism, not a secret. The full dataset — including
// items whose date hasn't arrived — ships inside the JS bundle, and the
// clock being compared against is the visitor's own system clock, which
// they can change. Anyone determined can read tomorrow's recipe today.
//
// That is fine for what this actually does: pace a catalogue's release so
// the site grows steadily instead of landing all at once. It would not be
// fine for anything that must stay confidential until a date — an embargo,
// an unreleased product, pricing. Those need a server.
// -----------------------------------------------------------------------

/**
 * Parse a publishAt value into a Date, defensively.
 *
 * Only ISO-8601 is safe here. Formats like "06-16-2025 9:25 am" parse in
 * V8 but return Invalid Date in Safari and Firefox — and an invalid date
 * fails every comparison silently, so the item would simply never appear
 * for those visitors, with nothing in the console to explain why.
 * A bad value returns null and the item is treated as already published,
 * which fails visible rather than fails hidden.
 */
export function parsePublishAt(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    if (typeof console !== "undefined") {
      console.warn(
        `[publishing] Unparseable publishAt: ${JSON.stringify(value)}. ` +
          `Use ISO-8601 ("2026-09-03" or "2026-09-03T09:00:00Z"). Treating as published.`
      );
    }
    return null;
  }
  return d;
}

export function isPublished(item, now = new Date()) {
  const at = parsePublishAt(item.publishAt);
  if (!at) return true;
  return at <= now;
}

export function publishedOnly(items, now = new Date()) {
  return items.filter((item) => isPublished(item, now));
}

/**
 * The soonest future publish time across a set of items, or null if
 * nothing is still pending. This is what lets the reveal happen without
 * polling: instead of re-filtering every second, schedule a single wake-up
 * for the exact moment the next item becomes live.
 */
export function nextBoundary(items, now = new Date()) {
  let soonest = null;
  for (const item of items) {
    const at = parsePublishAt(item.publishAt);
    if (!at || at <= now) continue;
    if (!soonest || at < soonest) soonest = at;
  }
  return soonest;
}

// setTimeout stores its delay in a signed 32-bit int. Anything above
// ~24.8 days overflows and fires immediately, in a loop. Cap the wait and
// re-arm when it expires — the boundary is recomputed each time anyway.
const MAX_TIMEOUT = 2147483647;

/**
 * Filters a list to what's published, and re-renders exactly when the next
 * item is due — no interval, no polling.
 *
 * A 1Hz interval would re-derive the whole list 86,400 times a day to
 * catch a boundary that crosses maybe once. With hundreds of recipes that's
 * millions of Date objects a day for no benefit, and it keeps the main
 * thread (and, on mobile, the radio and battery) awake for nothing. One
 * timer set to the exact boundary gives the identical result.
 *
 * @param {Array} items - anything with an optional `publishAt`
 * @returns {{ items: Array, now: Date, nextAt: Date|null }}
 */
export function usePublished(items) {
  const [now, setNow] = useState(() => new Date());

  const nextAt = useMemo(() => nextBoundary(items, now), [items, now]);

  useEffect(() => {
    if (!nextAt) return undefined;
    const delay = Math.min(Math.max(nextAt.getTime() - Date.now(), 0), MAX_TIMEOUT);
    const timer = setTimeout(() => setNow(new Date()), delay + 50);
    return () => clearTimeout(timer);
  }, [nextAt]);

  // A tab left open overnight gets a stale `now` if the machine was
  // suspended — the pending timeout doesn't fire during sleep. Re-check on
  // wake, which is when someone is actually looking at the page again.
  useEffect(() => {
    const recheck = () => setNow(new Date());
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);

  const visible = useMemo(() => publishedOnly(items, now), [items, now]);

  return { items: visible, now, nextAt };
}
